/**
 * What a scheduled report subscription may be created from (reports engine spec
 * §2, ISS-065 / T-201) and Eric's E1c launch decision for compare reports.
 * Pure, so the Reports page, the Subscribe drawer and tests share one rule.
 */
import type { ReportCriteria } from './reportCriteria';

export const COMPARE_NOT_SUPPORTED_REASON =
  "Scheduled emails don't support compare reports yet. Switch to Summary or Charts to subscribe.";

/** What the Reports page knows about the report on screen that the criteria alone cannot say. */
export interface SubscribeContext {
  /** The selected productions' span (groupDateSpan); used when "Use production dates" is on. */
  groupDateRange?: { start: string | null; end: string | null } | null;
  /** Whether any production or site is selected. */
  hasGroup?: boolean;
  /** A click-to-focus on a unit status narrows the report to units an email can't follow. */
  statusFocused?: boolean;
}

const USE_PRODUCTION_DATES = '“Use production dates”';

/**
 * Why this report cannot be subscribed to, or null when it can. Shown on the
 * Subscribe action and in the drawer, so it says what to change.
 */
export function subscribeBlockReason(criteria: ReportCriteria, context: SubscribeContext = {}): string | null {
  if (criteria.reportType === 'compare') return COMPARE_NOT_SUPPORTED_REASON;
  if (criteria.modelScope === 'selected' && criteria.modelCodes.length === 0) {
    return 'No models are selected, so this report is empty. Select at least one model to subscribe.';
  }
  if (criteria.industryScope === 'selected' && criteria.industryTypes.length === 0) {
    return 'No industries are selected, so this report is empty. Select at least one industry to subscribe.';
  }
  if (context.statusFocused) {
    return "Scheduled emails can't follow a status focus yet. Clear the status focus to subscribe.";
  }
  if (criteria.useFullGroup) {
    // The productions' span decides, not the stored preset: a custom range left
    // over from before would otherwise pass as "the production's dates".
    const span = context.groupDateRange
      ?? (criteria.datePreset === 'custom' ? criteria.customDateRange : null);
    const hasGroup = context.hasGroup
      ?? (criteria.groupScope === 'selected' && criteria.groupIds.length > 0);
    if (!hasGroup) {
      return `Select a production to use its dates, or turn off ${USE_PRODUCTION_DATES} to subscribe.`;
    }
    if (!span?.start || !span?.end) {
      const missing = !span?.start && !span?.end
        ? 'no start or end date'
        : !span?.end ? 'no end date' : 'no start date';
      return `This production has ${missing}. Turn off ${USE_PRODUCTION_DATES} and choose a date range to subscribe.`;
    }
    return null;
  }
  const { start, end } = criteria.customDateRange;
  if (criteria.datePreset === 'custom' && (!start || !end)) {
    return 'Choose both a start and an end date to subscribe.';
  }
  return null;
}

interface ProductionGroup {
  id: string;
  parent_group_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

/**
 * The date span "Use production dates" means for a subscription: the earliest
 * start to the latest end of the selected productions (a selected site counts
 * as its production). Any selected production without a start or an end date
 * leaves that bound unknown, so the subscription can't be resolved and is
 * blocked (spec §2). Dates are YYYY-MM-DD strings, compared as text.
 */
export function groupDateSpan(
  selectedGroupIds: readonly string[],
  groups: readonly ProductionGroup[],
): { start: string | null; end: string | null } {
  const selected = new Set(selectedGroupIds);
  const roots = groups.filter((g) => !g.parent_group_id && (
    selected.has(g.id) || groups.some((child) => child.parent_group_id === g.id && selected.has(child.id))
  ));
  if (roots.length === 0) return { start: null, end: null };
  const starts = roots.map((g) => g.start_date ?? null);
  const ends = roots.map((g) => g.end_date ?? null);
  return {
    start: starts.includes(null) ? null : (starts as string[]).reduce((a, b) => (a < b ? a : b)),
    end: ends.includes(null) ? null : (ends as string[]).reduce((a, b) => (a > b ? a : b)),
  };
}

const UNASSIGNED_PREFIX = '__unassigned_';
const UNASSIGNED_SUFFIX = '__';

/** The company a group selection id belongs to: a real group, or the Unassigned bucket's own company. */
function selectionCompany(groupId: string, groupCompanyById: ReadonlyMap<string, string>): string | undefined {
  if (groupId.startsWith(UNASSIGNED_PREFIX) && groupId.endsWith(UNASSIGNED_SUFFIX)) {
    return groupId.slice(UNASSIGNED_PREFIX.length, -UNASSIGNED_SUFFIX.length);
  }
  return groupCompanyById.get(groupId);
}

/** Why the subscription would cover the whole company without the user choosing that. */
export type AllUnitsNeeded = 'none_in_company' | 'no_selection' | null;

export interface CompanyProjection {
  criteria: ReportCriteria;
  /** Selection ids left out because they belong to another company (or to none we know). */
  droppedGroupIds: string[];
  /**
   * Set when the result would be every unit in the company although the user
   * didn't choose that: nothing selected belongs to it ('none_in_company'), or
   * nothing is selected at all ('no_selection'; the screen shows no report).
   * Subscribing then needs the explicit `allUnits` choice.
   */
  needsAllUnits: AllUnitsNeeded;
  /**
   * "Use production dates" can't be resolved for what is left: the remaining
   * productions lack a start or end date, or the subscription covers all units
   * (no productions to take dates from). Subscribing is blocked until it's
   * turned off.
   */
  datesUnresolved: boolean;
}

export const DATES_UNRESOLVED_REASON =
  'The productions left in this company have no start or end date to use. Turn off “Use production dates” and choose a date range to subscribe.';
export const DATES_ALL_UNITS_REASON =
  '“Use production dates” needs specific productions. Turn it off and choose a date range to subscribe to all units.';

/**
 * A subscription belongs to ONE company. Narrow the selection to it: other
 * companies' groups (and their Unassigned buckets) are dropped and listed, an
 * unknown group is dropped (fail closed), and an empty result is flagged
 * instead of silently widening to the whole company. Reporting on all of the
 * company's units is an explicit choice (`allUnits`).
 */
export function projectCriteriaToCompany(
  criteria: ReportCriteria,
  companyId: string,
  groupCompanyById: ReadonlyMap<string, string>,
  options: { allUnits?: boolean; groups?: readonly ProductionGroup[] } = {},
): CompanyProjection {
  const base = { ...criteria, companyIds: [companyId] };
  // Company-wide has no productions to take dates from.
  const companyWideDates = criteria.useFullGroup;
  if (options.allUnits) {
    return {
      criteria: { ...base, groupScope: 'all', groupIds: [] },
      droppedGroupIds: [], needsAllUnits: null, datesUnresolved: companyWideDates,
    };
  }
  if (criteria.groupScope === 'all') {
    return {
      criteria: { ...base, groupIds: [] },
      droppedGroupIds: [], needsAllUnits: 'no_selection', datesUnresolved: companyWideDates,
    };
  }
  const kept: string[] = [];
  const dropped: string[] = [];
  for (const id of criteria.groupIds) {
    (selectionCompany(id, groupCompanyById) === companyId ? kept : dropped).push(id);
  }
  let projected: ReportCriteria = { ...base, groupScope: 'selected', groupIds: kept };
  let datesUnresolved = false;
  // "Use production dates" was serialized as the span of EVERY selected
  // production; once others are dropped it must be the span of those kept.
  if (criteria.useFullGroup && dropped.length > 0 && kept.length > 0) {
    const span = options.groups ? groupDateSpan(kept, options.groups) : { start: null, end: null };
    if (span.start && span.end) {
      projected = { ...projected, datePreset: 'custom', customDateRange: { start: span.start, end: span.end } };
    } else {
      datesUnresolved = true;
    }
  }
  return {
    criteria: projected,
    droppedGroupIds: dropped,
    needsAllUnits: kept.length === 0 ? 'none_in_company' : null,
    datesUnresolved,
  };
}

/**
 * The company a new subscription starts on: the one most of the selected
 * productions belong to (so the user lands on their report, not an emptied
 * one), else the first option, else ''.
 */
export function defaultSubscriptionCompany(
  groupIds: readonly string[],
  groupCompanyById: ReadonlyMap<string, string>,
  options: readonly { id: string }[],
): string {
  const offered = new Set(options.map((o) => o.id));
  const counts = new Map<string, number>();
  for (const id of groupIds) {
    const company = selectionCompany(id, groupCompanyById);
    if (company && offered.has(company)) counts.set(company, (counts.get(company) ?? 0) + 1);
  }
  let best = '';
  let bestCount = 0;
  for (const option of options) {
    const n = counts.get(option.id) ?? 0;
    if (n > bestCount) { best = option.id; bestCount = n; }
  }
  return best || options[0]?.id || '';
}
